/**
 * Capacitor iframe back: when the storefront is embedded in the Express Capacitor app with
 * capacitor=true, read the URL and set `CAPACITOR_IFRAME_FLAG` on `window`, listen for
 * EXPRESS_REQUEST_IFRAME_BACK from the parent, and handle restore_path. Scope stays limited to that.
 *
 * We use the router's current route vs the "entry" path (path when iframe loaded), not window.history.length,
 * because the iframe can inherit a long browser history and history.length is not the Vue Router stack.
 *
 * Components: import `getIsCapacitorEmbed` (same pattern as `HANDWRITING_FLAG` / `handwritingMode.js`).
 */
const EXPRESS_REQUEST_IFRAME_BACK = "EXPRESS_REQUEST_IFRAME_BACK";
const EXPRESS_IFRAME_BACK_HANDLED = "EXPRESS_IFRAME_BACK_HANDLED";
const EXPRESS_IFRAME_BACK_NOT_HANDLED = "EXPRESS_IFRAME_BACK_NOT_HANDLED";

/** Set when in Capacitor iframe so PDP opens designer in parent, not iframe, after internal nav (URL may lose capacitor=true). */
export const CAPACITOR_IFRAME_FLAG = "__EXPRESS_CAPACITOR_IFRAME__";

/** True after the plugin set the flag (iframe + capacitor=true on load). */
export function getIsCapacitorEmbed() {
  return typeof window !== "undefined" && !!window[CAPACITOR_IFRAME_FLAG];
}

export default function capacitorIframeBackPlugin(app, { router }) {
  if (typeof window === "undefined" || window === window.top) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get("capacitor") !== "true") return;

  window[CAPACITOR_IFRAME_FLAG] = true;

  /** Path when the iframe first loaded. Set from URL now so first back isn't wrongly treated as "at entry". */
  const entryPath =
    typeof window.location.pathname === "string"
      ? window.location.pathname + (window.location.search || "")
      : "";

  /** URL-based restore: if parent added restore_path query (return from designer), push once and strip param. */
  router.isReady().then(() => {
    const params = new URLSearchParams(window.location.search);
    const restorePath = params.get("restore_path");
    if (restorePath) {
      const path = restorePath.startsWith("/") ? restorePath : "/" + restorePath;
      router.push(path).then(() => {
        params.delete("restore_path");
        const newSearch = params.toString();
        const url = window.location.pathname + (newSearch ? "?" + newSearch : "") + (window.location.hash || "");
        window.history.replaceState(window.history.state, "", url);
      });
    }
  });

  window.addEventListener("message", (event) => {
    if (event.data?.type !== EXPRESS_REQUEST_IFRAME_BACK) return;

    const currentPath = router.currentRoute.value.fullPath;
    const atEntry = entryPath !== "" && currentPath === entryPath;

    if (atEntry) {
      window.parent.postMessage({ type: EXPRESS_IFRAME_BACK_NOT_HANDLED }, "*");
    } else {
      router.back();
      window.parent.postMessage({ type: EXPRESS_IFRAME_BACK_HANDLED }, "*");
    }
  });
}
